import './NavBar.scss';
import { AiOutlineClose, AiOutlineMenu } from 'react-icons/ai';
import {
  ENTER_KEY_CODE,
  ROUTE_PROFILE_NAME,
  UBI_CONNECT
} from '../../utils/constants';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, NavLink } from 'react-router-dom';
import {
  selectAuth,
  selectIsCancelLogin,
  selectIsLoggingIn
} from '../../ducks/auth';
import Loader from '../../components/Loader/Loader';
import cn from 'classnames';
import incognitoIcon from '../../assets/images/incognito.webp';
import messages from './messages';
import { selectLocalSettings } from '../../ducks/settings';
import siteMessages from './siteMessages';
import ubisoftLogo from '../../assets/pictures/Ubisoft_logo.png';
import { useSelector } from 'react-redux';
import { useToggle } from 'react-use';

interface Props {
  onClickLogin: () => void;
  onClickLogout: () => void;
}

const NavBar = (props: Props) => {
  const auth = useSelector(selectAuth);
  const isLoggingIn = useSelector(selectIsLoggingIn);
  const isCancelLogin = useSelector(selectIsCancelLogin);
  const [showMenuMobile, toggleMenuMobile] = useToggle(false);
  const [showRightMenuMobile, toggleRightMenuMobile] = useToggle(false);

  const intl = useIntl();
  const lang = useSelector(selectLocalSettings);

  return (
    <nav className="navbar">
      <div className="navbar-desktop">
        <div className="navbar-desktop-container">
          <div className="navbar-desktop-left">
            <Link to={`/${lang}/`}>
              <img
                src={ubisoftLogo}
                alt={intl.formatMessage({ ...siteMessages.pictureLogo })}
                onClick={() => toggleMenuMobile(false)}
              />
            </Link>
          </div>
          <div className="navbar-desktop-center">
            {auth.ticket && (
              <NavLink
                to={`/${lang}/${ROUTE_PROFILE_NAME}`}
                className={({ isActive }) =>
                  cn(
                    'navbar-desktop-center-item',
                    isActive && 'navbar-desktop-center-item--active'
                  )
                }
              >
                <FormattedMessage {...messages.myProfile} />
              </NavLink>
            )}
            <NavLink
              to={`/${lang}/faq`}
              className={({ isActive }) =>
                cn(
                  'navbar-desktop-center-item',
                  isActive && 'navbar-desktop-center-item--active'
                )
              }
            >
              <FormattedMessage {...messages.faq} />
            </NavLink>
            <NavLink
              to={`/${lang}/contactus`}
              reloadDocument // We have to reload the page since the reCAPTCHA doesn't appear when navigating in tabs.
              className={({ isActive }) =>
                cn(
                  'navbar-desktop-center-item',
                  isActive && 'navbar-desktop-center-item--active'
                )
              }
            >
              <FormattedMessage {...messages.contactUs} />
            </NavLink>
          </div>

          <div className="navbar-desktop-right">
            {isLoggingIn && !isCancelLogin && (
              <div className="navbar-desktop-right-loading">
                <Loader />
              </div>
            )}
            {!auth.ticket && (!isLoggingIn || isCancelLogin) && (
              <>
                <img src={incognitoIcon} alt="" />
                <div
                  className="navbar-desktop-right-login"
                  onClick={props.onClickLogin}
                  tabIndex={0}
                  onKeyDown={(e: any) => {
                    if (e.keyCode == ENTER_KEY_CODE) e.target.click();
                  }}
                >
                  <FormattedMessage {...messages.login} />
                </div>
              </>
            )}
            {auth.ticket && !isLoggingIn && (
              <>
                <img
                  src={`${UBI_CONNECT.avatarURL}/${auth.userId}/default_146_146.png?appId=${UBI_CONNECT.appId}`}
                  alt=""
                />
                <div
                  className="navbar-desktop-right-logout"
                  onClick={props.onClickLogout}
                  tabIndex={0}
                  onKeyDown={(e: any) => {
                    if (e.keyCode == ENTER_KEY_CODE) e.target.click();
                  }}
                >
                  <FormattedMessage {...messages.logout} />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="navbar-mobile">
        {showMenuMobile && (
          <div className="navbar-mobile-menu">
            <NavLink
              end
              to={`/${lang}/`}
              className={({ isActive }) =>
                cn(
                  'navbar-mobile-menu-item',
                  isActive && 'navbar-mobile-menu-item--active'
                )
              }
              onClick={toggleMenuMobile}
              tabIndex={0}
              onKeyDown={(e: any) => {
                if (e.keyCode == ENTER_KEY_CODE) e.target.click();
              }}
            >
              <FormattedMessage {...messages.home} />
            </NavLink>
            <NavLink
              to={`/${lang}/faq`}
              className={({ isActive }) =>
                cn(
                  'navbar-mobile-menu-item',
                  isActive && 'navbar-mobile-menu-item--active'
                )
              }
              onClick={toggleMenuMobile}
              tabIndex={0}
              onKeyDown={(e: any) => {
                if (e.keyCode == ENTER_KEY_CODE) e.target.click();
              }}
            >
              <FormattedMessage {...messages.faq} />
            </NavLink>
            <NavLink
              to={`/${lang}/contactus`}
              className={({ isActive }) =>
                cn(
                  'navbar-mobile-menu-item',
                  isActive && 'navbar-mobile-menu-item--active'
                )
              }
              onClick={toggleMenuMobile}
              tabIndex={0}
              onKeyDown={(e: any) => {
                if (e.keyCode == ENTER_KEY_CODE) e.target.click();
              }}
            >
              <FormattedMessage {...messages.contactUs} />
            </NavLink>
          </div>
        )}
        {showRightMenuMobile && (
          <div className="navbar-mobile-menu-right">
            <NavLink
              to={`/${lang}/${ROUTE_PROFILE_NAME}`}
              className={({ isActive }) =>
                cn(
                  'navbar-mobile-menu-item',
                  isActive && 'navbar-mobile-menu-item--active'
                )
              }
              onClick={toggleRightMenuMobile}
              tabIndex={0}
              onKeyDown={(e: any) => {
                if (e.keyCode == ENTER_KEY_CODE) e.target.click();
              }}
            >
              <FormattedMessage {...messages.myProfile} />
            </NavLink>
            <div
              onClick={props.onClickLogout}
              tabIndex={0}
              onKeyDown={(e: any) => {
                if (e.keyCode == ENTER_KEY_CODE) e.target.click();
              }}
              className="navbar-mobile-menu-item"
            >
              <FormattedMessage {...messages.logout} />
            </div>
          </div>
        )}
        <div className="navbar-mobile-container">
          <div className="navbar-mobile-left">
            <div
              onClick={() => {
                toggleRightMenuMobile(false);
                toggleMenuMobile(!showMenuMobile);
              }}
              tabIndex={0}
              onKeyDown={(e: any) => {
                if (e.keyCode == ENTER_KEY_CODE) e.target.click();
              }}
            >
              {showMenuMobile ? <AiOutlineClose /> : <AiOutlineMenu />}
            </div>
          </div>
          <div className="navbar-mobile-center">
            <Link to={`/${lang}/`}>
              <img
                src={ubisoftLogo}
                alt={intl.formatMessage({ ...siteMessages.pictureLogo })}
                onClick={() => {
                  toggleMenuMobile(false);
                  toggleRightMenuMobile(false);
                }}
              />
            </Link>
          </div>

          <div className="navbar-mobile-right">
            {isLoggingIn && !isCancelLogin && (
              <div className="navbar-mobile-right-loading">
                <Loader />
              </div>
            )}
            {!auth.ticket && (!isLoggingIn || isCancelLogin) && (
              <img
                src={incognitoIcon}
                alt=""
                onClick={props.onClickLogin}
                tabIndex={0}
                onKeyDown={(e: any) => {
                  if (e.keyCode == ENTER_KEY_CODE) e.target.click();
                }}
              />
            )}
            {auth.ticket && !isLoggingIn && (
              <img
                className="navbar-mobile-right-user-avatar"
                src={`${UBI_CONNECT.avatarURL}/${auth.userId}/default_146_146.png?appId=${UBI_CONNECT.appId}`}
                alt={intl.formatMessage({ ...siteMessages.pictureUser })}
                onClick={() => {
                  toggleMenuMobile(false);
                  toggleRightMenuMobile(!showRightMenuMobile);
                }}
                tabIndex={0}
                onKeyDown={(e: any) => {
                  if (e.keyCode == ENTER_KEY_CODE) e.target.click();
                }}
              />
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default NavBar;
